import React, { useEffect, useState } from 'react';
import {
  Typography,
  TableContainer,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Avatar,
  useTheme,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { Edit } from '@mui/icons-material';
import { Achievement } from '@par/shared/common/types';
import { DetailedUserAchievement, Profile } from '@par/shared/types/player';
import { ProfileAchievementsDetailsDialog } from '../../dialog/ProfileAchievementsDetailsDialog';
import { AchievementsShowcaseDialog } from '../../dialog/AchievementsShowcaseDialog';

type PropTypes = {
  detailedUserAchievements?: DetailedUserAchievement[];
  onUpdateProfile: () => void;
  profile: Profile;
  viewProfile: Profile;
};

export const ProfileAchievementsTable = ({
  detailedUserAchievements,
  onUpdateProfile,
  profile,
  viewProfile,
}: PropTypes) => {
  const theme = useTheme();
  const [clickedAchievement, setClickedAchievement] = useState<Achievement>();
  const [openAchievementsDialog, setOpenAchievementsDialog] = useState(false);
  const [openAchievementsShowcaseDialog, setOpenAchievementsShowcaseDialog] =
    useState(false);
  const [showcase, setShowcase] = useState<DetailedUserAchievement[]>();

  useEffect(() => {
    if (!detailedUserAchievements) return;
    if (!viewProfile.achievementsShowcase) {
      setShowcase([]);
      return;
    }
    const showcase = detailedUserAchievements.filter((a) =>
      viewProfile.achievementsShowcase.includes(a.achievement?.name)
    );
    setShowcase(showcase);
  }, [detailedUserAchievements]);

  const handleRowClick = (achievement: Achievement) => {
    setClickedAchievement(achievement);
    setOpenAchievementsDialog(true);
  };

  const handleSelectShowcaseClick = () => {
    setOpenAchievementsShowcaseDialog(true);
  };

  return (
    <>
      {clickedAchievement ? (
        <ProfileAchievementsDetailsDialog
          achievement={clickedAchievement}
          close={() => setOpenAchievementsDialog(false)}
          open={openAchievementsDialog}
          viewProfile={viewProfile}
        />
      ) : null}
      {openAchievementsShowcaseDialog ? (
        <AchievementsShowcaseDialog
          detailedUserAchievements={detailedUserAchievements}
          achievementsShowcase={viewProfile.achievementsShowcase}
          close={() => setOpenAchievementsShowcaseDialog(false)}
          onUpdateProfile={onUpdateProfile}
          open={openAchievementsShowcaseDialog}
          profileId={profile.id}
        />
      ) : null}
      <Stack direction={'row'} alignItems="center" justifyContent="center">
        <EmojiEventsIcon sx={{ color: 'gold' }}></EmojiEventsIcon>
        <Typography
          variant="h5"
          width={'100%'}
          sx={{
            textShadow:
              theme.palette.mode === 'dark'
                ? `0 0 0px ${theme.palette.secondary.main}, 
            0 0 0px ${theme.palette.secondary.main}, 
            0px 0px 16px ${theme.palette.secondary.main}`
                : 'none',
          }}
        >
          Showcase
        </Typography>
        {viewProfile.id === profile.id ? (
          <>
            <Tooltip title="Select top 3" placement="top">
              <IconButton
                size="small"
                aria-label="edit"
                color="primary"
                sx={{
                  border: '1px solid rgba(205, 166, 140, 0.5)',
                  '&:hover': {
                    backgroundColor: '#8f7462',
                  },
                }}
                onClick={handleSelectShowcaseClick}
              >
                <Edit />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <EmojiEventsIcon
            sx={{ color: 'gold', display: { xs: 'none', md: 'initial' } }}
          ></EmojiEventsIcon>
        )}
      </Stack>
      <TableContainer
        component={Paper}
        sx={{ maxHeight: '400px', mt: 2, width: '100%' }}
      >
        <Table size="small" aria-label="simple table">
          <TableBody>
            {showcase?.length
              ? showcase.map((a) => (
                  <TableRow
                    key={a.achievement.displayName}
                    hover
                    onClick={() => handleRowClick(a.achievement)}
                    sx={{
                      cursor: 'pointer',
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    <TableCell>
                      <Avatar
                        src={a.achievement ? a.achievement.imageUrl : null}
                        // src={resizeImage(a.achievement.imagePublicId, 100, 100)}
                        variant="rounded"
                        sx={{
                          boxShadow: `0 0 5px 1px gold, 0 0 2px 3px gold inset`,
                        }}
                      ></Avatar>
                    </TableCell>
                    <TableCell>{a.achievement.displayName}</TableCell>
                    <TableCell>{a.count}</TableCell>
                  </TableRow>
                ))
              : null}
          </TableBody>
        </Table>
      </TableContainer>
      {!showcase?.length ? (
        <Typography>Not showcasing anything yet</Typography>
      ) : null}
    </>
  );
};
